import React, { useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { Button, Input } from "component-library";
import { ArrowLeftIcon } from "@heroicons/react/16/solid";
import { PraxisConfig } from "@shared/praxis-config";
import { CreatePraxisResult } from "@shared/praxis-admin";
import { adminPraxisRoute, routes } from "../../routing/routes";
import { getApiErrorMessage } from "../../api/api-error";
import { useCreatePraxis, useDefaultConfig } from "./hooks/usePraxes";
import { FieldHint, FormSection } from "./components/form-fields";
import { PageStatus } from "./components/admin-layout";
import { DomainSetupResultView } from "./components/domain-setup-result";
import {
  PraxisConfigFields,
  PraxisConfigFormShape,
} from "./praxis-config-form/praxis-config-fields";
import {
  readFileAsBase64,
  toConfigFormValues,
  toConfigInput,
} from "./praxis-config-form/config-form-values";

type NewPraxisFormValues = PraxisConfigFormShape & {
  subdomain: string;
  email: string;
  username: string;
  password: string;
};

const SUBDOMAIN = /^[a-z0-9]([a-z0-9-]*[a-z0-9])?$/;

// Firebase still verifies the domain and issues the certificate afterwards
const isDomainSetupInProgress = ({ domainSetup }: CreatePraxisResult) =>
  domainSetup.status === "done" || domainSetup.status === "pending";

const NewPraxisForm: React.FC<{
  defaults: PraxisConfig;
  onCreated: (result: CreatePraxisResult) => void;
}> = ({ defaults, onCreated }) => {
  const { t } = useTranslation();
  const createPraxis = useCreatePraxis();
  const form = useForm<NewPraxisFormValues>({
    defaultValues: {
      subdomain: "",
      email: "",
      username: "",
      password: "",
      config: toConfigFormValues(null, defaults),
      favicon: null,
    },
  });
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = form;

  const onSubmit = async (values: NewPraxisFormValues) => {
    const favicon = values.favicon?.[0];

    try {
      const result = await createPraxis.mutateAsync({
        subdomain: values.subdomain.trim(),
        email: values.email.trim(),
        username: values.username.trim(),
        password: values.password,
        config: toConfigInput(values.config),
        ...(favicon ? { faviconBase64: await readFileAsBase64(favicon) } : {}),
      });
      toast.success(
        isDomainSetupInProgress(result)
          ? t("admin.newPraxis.successWithDomainSetup")
          : t("admin.newPraxis.success"),
      );
      onCreated(result);
    } catch (error) {
      toast.error(getApiErrorMessage(error) ?? t("admin.newPraxis.error"));
    }
  };

  return (
    <FormProvider {...form}>
      <form
        onSubmit={handleSubmit(onSubmit)}
        className="flex flex-col gap-4"
        noValidate
      >
        <FormSection
          title={t("admin.newPraxis.praxis.title")}
          description={t("admin.newPraxis.praxis.description")}
        >
          <div className="grid gap-3 md:grid-cols-2">
            <Input
              id="subdomain"
              label={t("admin.newPraxis.praxis.subdomain")}
              placeholder="praxis-muster"
              error={errors.subdomain?.message}
              {...register("subdomain", {
                validate: (value) =>
                  SUBDOMAIN.test(value.trim()) ||
                  t("admin.newPraxis.praxis.invalidSubdomain"),
              })}
            />
            <Input
              id="email"
              type="email"
              label={t("admin.praxis.email")}
              placeholder="info@praxis.de"
              error={errors.email?.message}
              {...register("email", { required: t("admin.form.required") })}
            />
            <Input
              id="username"
              label={t("admin.newPraxis.praxis.username")}
              autoComplete="off"
              error={errors.username?.message}
              {...register("username", {
                validate: (value) =>
                  value.trim() !== "" || t("admin.form.required"),
              })}
            />
            <Input
              id="password"
              type="password"
              label={t("admin.newPraxis.praxis.password")}
              autoComplete="new-password"
              error={errors.password?.message}
              {...register("password", {
                required: t("admin.form.required"),
                minLength: {
                  value: 6,
                  message: t("admin.newPraxis.praxis.passwordMinLength"),
                },
              })}
            />
          </div>
        </FormSection>

        <PraxisConfigFields defaults={defaults} />

        <div className="flex justify-end">
          <Button type="submit" variant="contained" isLoading={isSubmitting}>
            {t("admin.newPraxis.submit")}
          </Button>
        </div>
      </form>
    </FormProvider>
  );
};

export const AdminNewPraxisPage: React.FC = () => {
  const { t } = useTranslation();
  const { data: defaults, isLoading, error } = useDefaultConfig();
  const [created, setCreated] = useState<CreatePraxisResult | null>(null);

  return (
    <>
      <Link
        to={routes.admin}
        className="flex w-fit items-center gap-1 text-sm text-primary hover:underline"
      >
        <ArrowLeftIcon className="h-4 w-4" />
        {t("admin.backToPraxes")}
      </Link>
      <h1 className="text-2xl text-text-primary">
        {t("admin.newPraxis.title")}
      </h1>

      {created ? (
        <FormSection
          title={t("admin.newPraxis.created", { subdomain: created.subdomain })}
        >
          <DomainSetupResultView result={created.domainSetup} />
          {isDomainSetupInProgress(created) && (
            <FieldHint>{t("admin.newPraxis.domainSetupDuration")}</FieldHint>
          )}
          <Link to={adminPraxisRoute(created.subdomain)} className="self-start">
            <Button variant="contained" tabIndex={-1}>
              {t("admin.newPraxis.openPraxis")}
            </Button>
          </Link>
        </FormSection>
      ) : (
        <>
          {isLoading && <PageStatus>{t("admin.loading")}</PageStatus>}
          {error && <PageStatus>{t("admin.defaultConfigError")}</PageStatus>}
          {defaults && (
            <NewPraxisForm defaults={defaults} onCreated={setCreated} />
          )}
        </>
      )}
    </>
  );
};
